import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Switch } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

interface CookiePreferences {
  strictlyNecessary: boolean;
  performance: boolean;
  targetingAndAdvertising: boolean;
  hasResponded: boolean;
}

const COOKIE_PREFERENCES_KEY = "womapp_cookie_preferences";

const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    strictlyNecessary: true, // Always active
    performance: false,
    targetingAndAdvertising: false,
    hasResponded: false,
  });
  const navigate = useNavigate();

  // Check local storage on mount
  useEffect(() => {
    const storedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (storedPreferences) {
      const parsed = JSON.parse(storedPreferences) as CookiePreferences;
      if (parsed.hasResponded) {
        // User has already made a choice, don't show banner
        setIsVisible(false);
        setPreferences(parsed);
      } else {
        // User closed without responding, show banner again
        setIsVisible(true);
      }
    } else {
      // First time visitor, show banner
      setIsVisible(true);
    }
  }, []);

  const handleToggle = (key: keyof CookiePreferences) => {
    if (key === "strictlyNecessary") return; // Cannot toggle strictly necessary
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleAccept = () => {
    const newPreferences: CookiePreferences = {
      ...preferences,
      performance: true,
      targetingAndAdvertising: true,
      hasResponded: true,
    };
    localStorage.setItem(
      COOKIE_PREFERENCES_KEY,
      JSON.stringify(newPreferences)
    );
    setIsVisible(false);
  };

  const handleDecline = () => {
    const newPreferences: CookiePreferences = {
      strictlyNecessary: true,
      performance: false,
      targetingAndAdvertising: false,
      hasResponded: true,
    };
    localStorage.setItem(
      COOKIE_PREFERENCES_KEY,
      JSON.stringify(newPreferences)
    );
    setIsVisible(false);
  };

  const handleClose = () => {
    // User closed without responding, store but mark as not responded
    const newPreferences: CookiePreferences = {
      ...preferences,
      hasResponded: false,
    };
    localStorage.setItem(
      COOKIE_PREFERENCES_KEY,
      JSON.stringify(newPreferences)
    );
    setIsVisible(false);
  };

  const handleCookiePolicyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/cookies");
    handleClose(); // Close banner when navigating to cookies page
  };

  if (!isVisible) return null;

  return (
    <BannerContainer>
      <BannerContent>
        {/* Header with close button */}
        <HeaderRow>
          <HeaderTitle>Our website uses cookies</HeaderTitle>
          <CloseButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </CloseButton>
        </HeaderRow>

        {/* Description */}
        <Description>
          Our website use cookies. By continuing, we assume your permission to
          deploy cookies as detailed in our{" "}
          <CookiePolicyLink href="/cookies" onClick={handleCookiePolicyClick}>
            Cookie Policy
          </CookiePolicyLink>
        </Description>

        {/* Cookie preferences */}
        <PreferencesContainer>
          {/* Strictly Necessary */}
          <PreferenceRow>
            <PreferenceLabel>Strictly Necessary</PreferenceLabel>
            <PreferenceStatus>Always active</PreferenceStatus>
          </PreferenceRow>

          {/* Performance */}
          <PreferenceRow>
            <PreferenceLabel>Performance</PreferenceLabel>
            <StyledSwitch
              checked={preferences.performance}
              onChange={() => handleToggle("performance")}
              color="primary"
            />
          </PreferenceRow>

          {/* Targeting and advertising */}
          <PreferenceRow>
            <PreferenceLabel>Targeting and advertising</PreferenceLabel>
            <StyledSwitch
              checked={preferences.targetingAndAdvertising}
              onChange={() => handleToggle("targetingAndAdvertising")}
              color="primary"
            />
          </PreferenceRow>
        </PreferencesContainer>

        {/* Action buttons */}
        <ButtonsContainer>
          <DeclineButton onClick={handleDecline}>Decline</DeclineButton>
          <AcceptButton onClick={handleAccept}>Accept cookies</AcceptButton>
        </ButtonsContainer>
      </BannerContent>
    </BannerContainer>
  );
};

// Styled Components
const BannerContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: "24px",
  right: "24px",
  zIndex: 9999,
  maxWidth: "360px",
  width: "calc(100% - 48px)",
  marginBottom: "80px",
  [theme.breakpoints.down("md")]: {
    marginBottom: "80px",
  },
}));

const BannerContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "24px",
  gap: "24px",
  background:
    theme.palette.mode === "dark"
      ? "rgba(42, 42, 42, 0.95)"
      : "rgba(209, 208, 198, 0.95)",
  backdropFilter: "blur(10px)",
  boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.3)",
  borderRadius: "24px",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.1)",
}));

const HeaderRow = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
});

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Montserrat",
  fontStyle: "normal",
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "133.4%",
  color: theme.palette.mode === "dark" ? "#E5E5E5" : "#1A1A1A",
  flex: 1,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  padding: "0px",
  color: theme.palette.mode === "dark" ? "#B6B6B6" : "#393939",
  backgroundColor: "transparent",
  border: "none",
  outline: "none",

  "& .MuiSvgIcon-root": {
    fontSize: "28px",
    fontWeight: "bold",
  },

  "&:hover": {
    backgroundColor: "transparent",
    color: theme.palette.mode === "dark" ? "#E5E5E5" : "#1A1A1A",
  },

  "&:focus": {
    outline: "none",
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: "Roboto",
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "157%",
  letterSpacing: "0.1px",
  color: theme.palette.mode === "dark" ? "#B6B6B6" : "#393939",
}));

const CookiePolicyLink = styled("a")(({ theme }) => ({
  fontFamily: "Roboto",
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "157%",
  letterSpacing: "0.1px",
  color: theme.palette.mode === "dark" ? "#00FFFF" : "#005CAD",
  textDecoration: "none",
  cursor: "pointer",

  "&:hover": {
    textDecoration: "underline",
  },
}));

const PreferencesContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const PreferenceRow = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
});

const PreferenceLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "Roboto",
  fontStyle: "normal",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "157%",
  letterSpacing: "0.1px",
  color: theme.palette.mode === "dark" ? "#E5E5E5" : "#1A1A1A",
  flex: 1,
}));

const PreferenceStatus = styled(Typography)(({ theme }) => ({
  fontFamily: "Roboto",
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "157%",
  letterSpacing: "0.1px",
  color: theme.palette.mode === "dark" ? "#B6B6B6" : "#393939",
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 58,
  height: 28,
  padding: 0,

  "& .MuiSwitch-switchBase": {
    padding: 2,
    margin: 0,
    transitionDuration: "300ms",

    "&.Mui-checked": {
      transform: "translateX(30px)",
      color:
        theme.palette.mode === "dark"
          ? "rgba(42, 42, 42, 0.95)"
          : "rgba(209, 208, 198, 0.95)",

      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#00FFFF" : "#005CAD",
        opacity: 1,
        border: 0,
      },

      "& .MuiSwitch-thumb": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(42, 42, 42, 0.95)"
            : "rgba(209, 208, 198, 0.95)",
      },
    },
  },

  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 24,
    height: 24,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(42, 42, 42, 0.95)"
        : "rgba(209, 208, 198, 0.95)",
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.3)",
  },

  "& .MuiSwitch-track": {
    borderRadius: 28 / 2,
    backgroundColor: theme.palette.mode === "dark" ? "#4A4A4A" : "#999999",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const ButtonsContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  gap: "12px",
  justifyContent: "space-between",
  width: "100%",
});

// Base button styles shared by both buttons
const baseButtonStyles = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 24px",
  height: "40px",
  fontFamily: "Montserrat",
  fontStyle: "normal",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "1",
  textAlign: "center" as const,
  border: "none",
  borderRadius: "64px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  whiteSpace: "nowrap" as const,
};

const DeclineButton = styled("button")(({ theme }) => ({
  ...baseButtonStyles,
  color: theme.palette.mode === "dark" ? "#E5E5E5" : "#393939",
  background:
    theme.palette.mode === "dark"
      ? "rgba(153, 153, 153, 0.4)"
      : "rgba(153, 153, 153, 0.4)",
  backdropFilter: "blur(6px)",
  boxShadow: "0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12)",

  "&:hover": {
    background:
      theme.palette.mode === "dark"
        ? "rgba(153, 153, 153, 0.5)"
        : "rgba(153, 153, 153, 0.5)",
  },
}));

const AcceptButton = styled("button")(({ theme }) => ({
  ...baseButtonStyles,
  color: "#FFFFFF",
  background:
    theme.palette.mode === "dark"
      ? "rgba(0, 36, 58, 0.8)"
      : "rgba(0, 36, 58, 0.8)",
  backdropFilter: "blur(6px)",
  boxShadow: "0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12)",

  "&:hover": {
    background:
      theme.palette.mode === "dark"
        ? "rgba(0, 36, 58, 0.9)"
        : "rgba(0, 36, 58, 0.9)",
  },
}));

export default CookieBanner;
